import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { TouchableOpacity } from 'react-native';

import { Box, Inline } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import analytics from '../../../modules/analytics';
import AvatarIcon from '../../AvatarIcon';
import {
  BodyText, Icon,
} from '../../ui';
import Button from '../../ui/Button';
import {
  NOTIFICATION_VIEWED_EVENT,
  getFromNowDisplay,
  createNotificationParams,
  trackNotificationClick,
} from '../../../modules/notificationModule';

/**
 * This will be our standard notification that can be used as a base to create our notifications.
 * The entire notification can either be a button or statelink. It is a button by default.
 * @example
 * import NotificationBanner from '../../NotificationBanner';
 * import ICON from '../../images/icons/myNotificationIcon.svg';
 *
 * const MyNewNotification = ({ notification }) => {
 *   const buttonClickTracking = {
 *     name: 'pro_mynewnotification_clicked',
 *     params: {
 *       notification_type: 'the_notification_type',
 *     }
 *   };
 *
 *   const buttonProps = {
 *     text: 'my btn text'
 *     onClick: () => {},
 *     clickTracking: buttonClickTracking,
 *     appearance: 'text-light',
 *     size: 'small',
 *   };
 *
 *   return (
 *     <NotificationBanner
 *       notification={notification}
 *       icon={ICON}
 *       highlightedText="This is the highlighted text portion"
 *       text="This is the text portion"
 *       subText="The sub text part"
 *       buttonProps={buttonProps}
 *     />
 *   )
 * }
 */
const NotificationBanner = ({
  notification,
  icon,
  iconColor,
  iconSize,
  highlightedText,
  text,
  highlightBorderColor,
  highlightedSubText,
  subText,
  buttonProps,
  textLinkProps,
  showAvatarIcon,
  handleNotificationClick,
  avatarIconBorderColor,
  avatarBadgeAppearance,
  avatarBadgeText,
}) => {
  const notificationCreationTime = notification.creation_time;

  useEffect(() => {
    const notificationParams = createNotificationParams(notification);
    analytics.track(NOTIFICATION_VIEWED_EVENT, notificationParams);
  });

  const onNotificationClick = () => {
    if (handleNotificationClick) {
      handleNotificationClick();
    }
    trackNotificationClick(notification);
  };

  let IconComponent;

  if (icon && typeof icon === 'function') {
    IconComponent = icon;
  }

  return (
    <TouchableOpacity
      testID="notification-wrapper"
      onClick={onNotificationClick}
      activeOpacity={1}
    >
      <Inline>
        <Box
          minWidth={75}
          paddingTop={16}
          paddingLeft={20}
          testID={showAvatarIcon ? 'notification-avatar-icon-container' : 'notification-icon-container'}
        >
          {showAvatarIcon && (
            <AvatarIcon
              icon={notification?.client_thumbnail}
              name={notification?.client_name}
              sizeClass="small"
              badge={avatarBadgeText}
              badgeAppearance={avatarBadgeAppearance}
              iconBorderColor={avatarIconBorderColor}
            />
          )}

          {!showAvatarIcon && (
            <Box>
              {IconComponent
                ? <IconComponent width={iconSize} height={iconSize} color={iconColor} />
                : (
                  <Icon
                    uri={icon}
                    size={iconSize}
                    testID="notification-icon"
                  />
                )}
            </Box>
          )}
        </Box>
        <Box
          paddingBottom={buttonProps ? 44 : 20}
          borderColor={colors.lightGrey}
          borderStyle="solid"
          borderBottomWidth={1}
          style={{
            width: '100%',
          }}
        >
          {/* Main Text Section */}
          {(highlightedText || text) && (
            <Box
              paddingTop={16}
              maxWidth={280}
              style={{ marginBottom: '2px' }}
              testID="main-text-section"
            >
              <BodyText
                color={colors.blackInBlack}
                appearance="normal"
              >
                {!!highlightedText && (
                  <BodyText
                    weight="bold"
                    appearance="normal"
                    style={{ marginRight: '4px' }}
                    testID="highlighted-main-text"
                  >
                    {highlightedText}
                  </BodyText>
                )}
                {!!text && (
                  <BodyText
                    appearance="normal"
                    testID="main-text"
                  >
                    {text}
                  </BodyText>
                )}
              </BodyText>
            </Box>
          )}

          {!!notificationCreationTime && (
            <BodyText
              appearance="smaller"
              weight="normal"
              color={colors.silver}
              textTransform="uppercase"
              testID="notification-display-timestamp"
            >
              {getFromNowDisplay(notificationCreationTime)}
            </BodyText>
          )}
          {/* Subtext section */}
          {(highlightedSubText || subText) && (
            <Box
              paddingTop={12}
              testID="sub-text-section"
            >
              <Inline>
                <Box
                  minWidth={4}
                  color={highlightBorderColor}
                  style={{
                    borderRadius: '2px',
                  }}
                  testID="sub-text-left-border"
                />
                <Box
                  paddingLeft={8}
                  maxWidth={270}
                >
                  <BodyText
                    color={colors.blackInBlack}
                    appearance="small"
                  >
                    {!!highlightedSubText && (
                      <BodyText
                        weight="bold"
                        appearance="small"
                        style={{ marginRight: '4px' }}
                        testID="highlighted-sub-text"
                      >
                        {highlightedSubText}
                      </BodyText>
                    )}
                    {!!subText && (
                      <BodyText
                        appearance="small"
                        testID="sub-text"
                      >
                        {subText}
                      </BodyText>
                    )}
                  </BodyText>
                  {textLinkProps && (
                    <Box
                      alignItems="flex-start"
                    >
                      <Button
                        link={textLinkProps.link}
                        onClick={textLinkProps.onClick}
                        clickTracking={textLinkProps.clickTracking}
                        linkParams={textLinkProps.linkParams}
                        size="small"
                        appearance="none"
                        stopPropagation={textLinkProps.stopPropagation}
                        testID="notification-text-link"
                      >
                        <BodyText
                          textTransform="none"
                          appearance={textLinkProps.appearance}
                          weight={textLinkProps.weight}
                          textDecoration="underline"
                        >
                          {textLinkProps.text}
                        </BodyText>
                      </Button>
                    </Box>
                  )}
                </Box>
              </Inline>
            </Box>
          )}
        </Box>
        {buttonProps && (
          <Box
            style={{
              position: 'absolute',
              bottom: '8px',
              left: '75px',
            }}
          >
            <Button
              text={buttonProps.text}
              link={buttonProps.link}
              onClick={buttonProps.onClick}
              clickTracking={buttonProps.clickTracking}
              linkParams={buttonProps.linkParams}
              size={buttonProps.size}
              appearance={buttonProps.appearance}
              testID="notification-button"
              stopPropagation
            />
          </Box>
        )}
      </Inline>
    </TouchableOpacity>
  );
};

NotificationBanner.propTypes = {
  /** The notification object */
  notification: PropTypes.object.isRequired,
  /** The notification icon */
  icon: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.string,
  ]),
  /** Size of icon */
  iconSize: PropTypes.number,
  /** Color of icon */
  iconColor: PropTypes.string,
  /** The bolded part of the main text of the notification */
  highlightedText: PropTypes.string,
  /** Main text of the notification */
  text: PropTypes.string,
  /** The left border color of the subtext area */
  highlightBorderColor: PropTypes.string,
  /** The bolded part of the subtext */
  highlightedSubText: PropTypes.string,
  /** The subtext of the notification */
  subText: PropTypes.string,
  /** Props for the button inside the notification, if needed */
  buttonProps: PropTypes.object,
  /** Props for the text link inside the subtext section, if needed */
  textLinkProps: PropTypes.object,
  /** Show the avatar icon */
  showAvatarIcon: PropTypes.bool,
  /** handles the click event of entire notification */
  handleNotificationClick: PropTypes.func,
  /** the avatar icon border color if showing avatar icon */
  avatarIconBorderColor: PropTypes.string,
  /** the avatar badge appearance if showing the avatar icon */
  avatarBadgeAppearance: PropTypes.string,
  /** the text inside the avatar icon if showing the avatar icon */
  avatarBadgeText: PropTypes.string,
};

NotificationBanner.defaultProps = {
  icon: '',
  iconColor: null,
  iconSize: 36,
  highlightedText: '',
  text: '',
  highlightBorderColor: colors.spark,
  highlightedSubText: '',
  subText: '',
  buttonProps: undefined,
  textLinkProps: undefined,
  showAvatarIcon: false,
  handleNotificationClick: undefined,
  avatarIconBorderColor: undefined,
  avatarBadgeAppearance: undefined,
  avatarBadgeText: undefined,
};

export default NotificationBanner;
